import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import PageHeader from '@/components/PageHeader';
import { AdminPageSections, AutoRefreshToolbar, CollapsiblePanel } from '@/components/common';
import UpgradePlanSection from '@/components/UpgradePlanSection';
import { DataTable, TruncatedCell, type DataTableColumn } from '@/components/table';
import {
  DeliveryStatusBadge,
  EmptyState,
  ErrorBanner,
  MetricChip,
  SourceChips,
  StatusBadge,
} from '@/components/ui';
import { useMe } from '@/context/MeContext';
import { platformRolePlanLabel } from '@/utils/capabilities';
import { isPlatformStaffRole } from '@/utils/teamRoles';
import { useAutoRefreshPolling } from '@/hooks/useAutoRefreshPolling';
import { useDiagnosticsPolling } from '@/hooks/useDiagnosticsPolling';
import { AttentionCollapsibleSection } from '@/components/activity/AttentionPanel';
import { RecentActivityCollapsibleSection } from '@/components/activity/RecentActivityWidget';
import DiagnosticsPanel from '@/components/diagnostics/DiagnosticsPanel';
import type { BillingStatus, DashboardSummary, HealthData, UsageSummary } from '@/types';
import {
  formatJobType,
  jobStatusLabel,
  jobStatusVariant,
} from '@/utils/jobStatus';

function healthStatusClass(status: string | undefined): string {
  switch (status) {
    case 'healthy':
      return 'stat-ok';
    case 'degraded':
      return 'stat-degraded';
    case 'unhealthy':
      return 'stat-unhealthy';
    default:
      return 'stat';
  }
}

function healthBadgeClass(status: string | undefined): string {
  switch (status) {
    case 'healthy':
      return 'badge-ok';
    case 'degraded':
      return 'badge-degraded';
    case 'unhealthy':
      return 'badge-bad';
    default:
      return 'badge-muted';
  }
}

function formatStatusLabel(status: string | undefined): string {
  if (!status) return '…';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function formatDuration(seconds?: number | null): string {
  if (seconds == null || Number.isNaN(seconds)) return '—';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${(seconds / 60).toFixed(1)}m`;
}

function workerVariant(status: string, stale: boolean): 'success' | 'warning' | 'danger' | 'neutral' {
  if (stale) return 'warning';
  if (status === 'healthy' || status === 'idle') return 'success';
  if (status === 'busy') return 'neutral';
  if (status === 'error' || status === 'stale') return 'danger';
  return 'warning';
}

function sourceHealthVariant(status?: string | null): 'success' | 'warning' | 'danger' | 'neutral' {
  const normalized = (status || '').toUpperCase();
  if (normalized === 'HEALTHY' || normalized === 'OK') return 'success';
  if (normalized.includes('COOLDOWN') || normalized.includes('DEGRADED')) return 'warning';
  if (normalized.includes('FAIL') || normalized.includes('BLOCK')) return 'danger';
  return 'neutral';
}

function roleLabel(role: string): string {
  switch (role) {
    case 'scrape-worker':
      return 'Scrape worker';
    case 'send-worker':
      return 'Send worker';
    case 'scheduler':
      return 'Scheduler';
    default:
      return role;
  }
}

export default function SystemDashboard() {
  const { me } = useMe();
  const canViewDiagnostics = me?.permissions.canViewDiagnostics ?? false;
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [health, setHealth] = useState<HealthData | null>(null);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [error, setError] = useState('');
  const {
    diagnostics,
    lastUpdatedAt,
    loading: diagnosticsLoading,
    autoRefreshing: diagnosticsRefreshing,
    error: diagnosticsError,
    autoRefresh: diagnosticsAutoRefresh,
  } = useDiagnosticsPolling({ enabled: canViewDiagnostics });

  const load = useCallback(async () => {
    const [s, h, u, b] = await Promise.all([
      api.dashboard.summary(),
      api.dashboard.health(),
      api.usage.platformSummary().catch(() => null),
      api.billing.status().catch(() => null),
    ]);
    setSummary(s);
    setHealth(h);
    setUsage(u);
    setBilling(b);
    setError('');
  }, []);

  const { autoRefresh: dashboardAutoRefresh, initialLoading } = useAutoRefreshPolling({
    hasData: () => summary !== null,
    load: async () => {
      try {
        await load();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load dashboard');
        throw e;
      }
    },
    autoRefresh: {
      intervalMs: 15000,
      storageKey: 'dashboard-auto-refresh',
    },
  });

  const cards = summary?.cards;
  const healthClass = healthStatusClass(health?.status);
  const billingWarning = health?.billingHealth?.status === 'warning';
  const billingBypass =
    health?.billingBypass ?? me?.billingBypass ?? isPlatformStaffRole(me?.role ?? '');

  const planOverLimit =
    usage &&
    (usage.resources.searches.current > usage.resources.searches.limit ||
      usage.resources.subscriptions.current > usage.resources.subscriptions.limit ||
      usage.resources.sources.current > usage.resources.sources.limit);

  const sourceColumns = useMemo<DataTableColumn<DashboardSummary['source_effectiveness'][number]>[]>(
    () => [
      {
        id: 'source',
        header: 'Source',
        width: 140,
        render: (row) => <SourceChips sources={[row.source_name]} variant="compact" />,
      },
      {
        id: 'health',
        header: 'Health',
        width: 110,
        render: (row) => (
          <StatusBadge variant={sourceHealthVariant(row.health_status)}>{row.health_status || 'unknown'}</StatusBadge>
        ),
      },
      {
        id: 'links',
        header: 'New links (24h)',
        width: 110,
        render: (row) => row.new_links_24h,
      },
      {
        id: 'scrape_ok',
        header: 'Scrape OK',
        width: 90,
        render: (row) => row.scrape_success_24h,
      },
      {
        id: 'scrape_fail',
        header: 'Scrape fail',
        width: 90,
        render: (row) => row.scrape_failures_24h,
      },
      {
        id: 'avg',
        header: 'Avg scrape',
        width: 100,
        render: (row) => formatDuration(row.avg_scrape_duration_seconds),
      },
      {
        id: 'dupes',
        header: 'Dupes skipped',
        width: 110,
        render: (row) => row.duplicates_skipped_24h,
      },
      {
        id: 'filtered',
        header: 'Filtered out',
        width: 100,
        render: (row) => row.filtered_out_24h,
      },
      {
        id: 'unsupported',
        header: 'Unsupported filt.',
        width: 120,
        render: (row) => row.unsupported_filter_events_24h,
      },
    ],
    []
  );

  return (
    <div className="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle="System overview — jobs, deliveries, sources, and workers"
      >
        <Link to="/jobs" className="btn btn-ghost">
          Jobs
        </Link>
        <Link to="/activity" className="btn btn-ghost">
          Activity
        </Link>
        <AutoRefreshToolbar
          control={dashboardAutoRefresh}
          intervalOptions={[
            { value: 10000, label: 'Every 10s' },
            { value: 15000, label: 'Every 15s' },
            { value: 30000, label: 'Every 30s' },
          ]}
        />
      </PageHeader>

      {error ? <ErrorBanner message={error} /> : null}
      {dashboardAutoRefresh.error ? <ErrorBanner message={dashboardAutoRefresh.error} /> : null}
      {canViewDiagnostics && diagnosticsError ? <ErrorBanner message={diagnosticsError} /> : null}

      <AdminPageSections className="dashboardPanels">
        {(me?.permissions.canViewWorkspaceActivity ?? me?.permissions.canViewCustomerActivity) ? (
          <>
            <AttentionCollapsibleSection
              storageKey="system-attention"
              storageScope="dashboard"
              refreshSignal={dashboardAutoRefresh.lastUpdatedAt}
            />
            <RecentActivityCollapsibleSection
              storageKey="system-recent-activity"
              storageScope="dashboard"
              limit={5}
              refreshSignal={dashboardAutoRefresh.lastUpdatedAt}
            />
          </>
        ) : null}

        <CollapsiblePanel id="system-summary" storageScope="dashboard" title="System summary" defaultExpanded>
          {initialLoading && !summary ? (
            <div className="loading-inline" role="status">
              <span className="spinner" aria-hidden />
              Loading summary…
            </div>
          ) : cards ? (
            <>
              <div className="metric-chips metric-chips--wrap">
                <MetricChip label="Active jobs" value={cards.active_jobs} variant={cards.active_jobs > 0 ? 'neutral' : 'success'} />
                <MetricChip label="Queued items" value={cards.queued_job_items} variant={cards.queued_job_items > 50 ? 'warning' : 'neutral'} />
                <MetricChip label="Processing items" value={cards.processing_job_items} />
                <MetricChip label="Failed items" value={cards.failed_job_items} variant={cards.failed_job_items ? 'danger' : 'success'} />
                <MetricChip label="Dead letter" value={cards.dead_letter_job_items} variant={cards.dead_letter_job_items ? 'danger' : 'success'} />
                <MetricChip label="Scrape OK (24h)" value={cards.scrape_success_24h} variant="success" />
                <MetricChip label="Scrape fail (24h)" value={cards.scrape_failure_24h} variant={cards.scrape_failure_24h ? 'danger' : 'success'} />
                <MetricChip label="Send OK (24h)" value={cards.send_success_24h} variant="success" />
                <MetricChip label="Send fail (24h)" value={cards.send_failure_24h} variant={cards.send_failure_24h ? 'danger' : 'success'} />
                <MetricChip label="New links (24h)" value={cards.new_links_24h} />
                <MetricChip label="New links today" value={cards.new_links_today} />
                <MetricChip label="Enabled searches" value={cards.enabled_searches} />
                <MetricChip label="Enabled recipients" value={cards.enabled_recipients} />
                <MetricChip label="Source errors" value={cards.source_errors} variant={cards.source_errors ? 'danger' : 'success'} />
              </div>
              {summary?.queues ? (
                <p className="muted dashboard-queue-hint">
                  Wake queues — scrape: {summary.queues.scrape_wake_depth}, send: {summary.queues.send_wake_depth}
                </p>
              ) : null}
            </>
          ) : null}
        </CollapsiblePanel>

        <CollapsiblePanel id="workers" storageScope="dashboard" title="Worker status" defaultExpanded>
          {summary?.workers?.length ? (
            <div className="dashboard-worker-grid">
              {summary.workers.map((worker) => (
                <div key={worker.role} className="card dashboard-worker-card">
                  <div className="dashboard-worker-card__header">
                    <strong>{roleLabel(worker.role)}</strong>
                    <StatusBadge variant={workerVariant(worker.status, worker.stale)}>
                      {worker.stale ? 'Stale' : worker.status}
                    </StatusBadge>
                  </div>
                  <dl className="dashboard-worker-card__meta">
                    <div>
                      <dt>Active</dt>
                      <dd>{worker.active_count}</dd>
                    </div>
                    <div>
                      <dt>Last heartbeat</dt>
                      <dd>
                        {worker.last_heartbeat_at
                          ? new Date(worker.last_heartbeat_at).toLocaleString()
                          : '—'}
                      </dd>
                    </div>
                    <div className="dashboard-worker-card__full">
                      <dt>Current job</dt>
                      <dd>{worker.current_job || '—'}</dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="No worker heartbeats" description="Workers report status when scheduler and job workers are running." />
          )}
        </CollapsiblePanel>

        <CollapsiblePanel
          id="source-effectiveness"
          storageScope="dashboard"
          title="Source effectiveness (24h)"
          subtitle="Use this to see why a source like Mobile.de is not producing links"
          defaultExpanded
        >
          {summary?.source_effectiveness?.length ? (
            <DataTable
              columns={sourceColumns}
              rows={summary.source_effectiveness}
              getRowKey={(row) => row.source_name}
              columnStorageKey="dashboard-source-effectiveness"
              emptyState={<EmptyState title="No sources" description="Enable sources to see effectiveness metrics." />}
            />
          ) : (
            <EmptyState title="No source data" description="Scrape runs and discoveries will populate this table." />
          )}
        </CollapsiblePanel>

        <CollapsiblePanel id="recent-jobs" storageScope="dashboard" title="Recent jobs" badge={summary?.recent_jobs.length} defaultExpanded={false}>
          {summary?.recent_jobs.length ? (
            <div className="table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Items</th>
                    <th>Created</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {summary.recent_jobs.map((job) => (
                    <tr key={job.id}>
                      <td>{formatJobType(job.type || '')}</td>
                      <td>
                        <StatusBadge variant={jobStatusVariant(job.status || '')}>
                          {jobStatusLabel(job.status || '')}
                        </StatusBadge>
                      </td>
                      <td>
                        {job.total_items}
                        {job.failed_items ? ` (${job.failed_items} failed)` : ''}
                      </td>
                      <td>{job.created_at ? new Date(job.created_at).toLocaleString() : '—'}</td>
                      <td>
                        <Link to={`/jobs?job_id=${job.id}`} className="btn btn-sm btn-ghost">
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No recent jobs" description="Jobs appear when scrapes or send batches are enqueued." action={<Link to="/jobs" className="btn btn-sm">Open jobs</Link>} />
          )}
        </CollapsiblePanel>

        <CollapsiblePanel id="recent-failed-jobs" storageScope="dashboard" title="Recent failed jobs" badge={summary?.recent_failed_jobs.length} defaultExpanded={Boolean(summary?.recent_failed_jobs.length)}>
          {summary?.recent_failed_jobs.length ? (
            <div className="table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Error</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.recent_failed_jobs.map((job) => (
                    <tr key={job.id} className="row-failed">
                      <td>{formatJobType(job.type || '')}</td>
                      <td>
                        <StatusBadge variant={jobStatusVariant(job.status || '')}>
                          {jobStatusLabel(job.status || '')}
                        </StatusBadge>
                      </td>
                      <td><TruncatedCell value={job.error || '—'} /></td>
                      <td>{job.created_at ? new Date(job.created_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted">No failed jobs recently.</p>
          )}
        </CollapsiblePanel>

        <CollapsiblePanel id="recent-discoveries" storageScope="dashboard" title="Recent discoveries" badge={summary?.recent_discoveries.length} defaultExpanded={false}>
          {summary?.recent_discoveries.length ? (
            <div className="table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Title</th>
                    <th>Time</th>
                    <th>URL</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.recent_discoveries.map((row) => (
                    <tr key={row.id}>
                      <td>{row.source_name ? <SourceChips sources={[row.source_name]} variant="compact" /> : '—'}</td>
                      <td><TruncatedCell value={row.title} /></td>
                      <td>{row.discovered_at ? new Date(row.discovered_at).toLocaleString() : '—'}</td>
                      <td><TruncatedCell value={row.url} href={row.url} copyable /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No discoveries yet" description="Links show up after successful scrapes." />
          )}
        </CollapsiblePanel>

        <CollapsiblePanel id="recent-deliveries" storageScope="dashboard" title="Recent deliveries" badge={summary?.recent_deliveries.length} defaultExpanded={false}>
          {summary?.recent_deliveries.length ? (
            <div className="table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Search</th>
                    <th>Source</th>
                    <th>Time</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.recent_deliveries.map((row) => (
                    <tr key={row.id} className={row.status === 'FAILED' ? 'row-failed' : undefined}>
                      <td><DeliveryStatusBadge status={row.status} /></td>
                      <td><TruncatedCell value={row.search_name} /></td>
                      <td>{row.source_name ? <SourceChips sources={[row.source_name]} variant="compact" /> : '—'}</td>
                      <td>{row.sent_at ? new Date(row.sent_at).toLocaleString() : '—'}</td>
                      <td><TruncatedCell value={row.error_message} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No deliveries yet" description="Notifications appear after the send worker runs." action={<Link to="/activity" className="btn btn-sm">Open activity</Link>} />
          )}
        </CollapsiblePanel>

        <CollapsiblePanel id="recent-source-errors" storageScope="dashboard" title="Recent source errors" badge={summary?.recent_source_errors.length} defaultExpanded={Boolean(summary?.recent_source_errors.length)}>
          {summary?.recent_source_errors.length ? (
            <div className="table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Message</th>
                    <th>Last failure</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.recent_source_errors.map((row) => (
                    <tr key={row.source_name}>
                      <td><SourceChips sources={[row.source_name]} variant="compact" /></td>
                      <td><StatusBadge variant={sourceHealthVariant(row.status)}>{row.status || 'unknown'}</StatusBadge></td>
                      <td><TruncatedCell value={row.message} /></td>
                      <td>{row.last_failure_at ? new Date(row.last_failure_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted">All sources healthy in the last check.</p>
          )}
        </CollapsiblePanel>

        <CollapsiblePanel id="health-stats" storageScope="dashboard" title="App health" defaultExpanded={false}>
          <div className="grid stats">
            <div className={`card stat stat-card ${healthClass}`}>
              <div className="stat-top">
                <span className="label">Overall health</span>
              </div>
              <div className="stat-value text">
                <span className={`badge ${healthBadgeClass(health?.status)}`}>
                  {formatStatusLabel(health?.status)}
                </span>
              </div>
              {health?.reasons && health.reasons.length > 0 ? (
                <ul className="health-reasons" aria-label="Health details">
                  {health.reasons.slice(0, 5).map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </CollapsiblePanel>

        {(usage || billing) && (
          <CollapsiblePanel id="usage-plan" storageScope="dashboard" title="Usage / plan" defaultExpanded={Boolean(planOverLimit)}>
            <section className={`usage-plan-card ${billingWarning && !billingBypass ? 'usage-plan-card--warn' : ''}`}>
              {billingBypass ? (
                <p className="usage-plan-banner usage-plan-banner--bypass">
                  {platformRolePlanLabel(me) || 'N/A'}
                </p>
              ) : null}
              {!billingBypass && planOverLimit ? (
                <p className="usage-plan-banner usage-plan-banner--limit">
                  Usage over plan limits
                </p>
              ) : null}
              {usage && !billingBypass ? (
                <>
                  <p className="muted usage-plan-label">
                    Plan: <strong>{usage.plan.charAt(0).toUpperCase() + usage.plan.slice(1)}</strong>
                  </p>
                  <ul className="usage-plan-stats">
                    <li>Searches: {usage.resources.searches.current} / {usage.resources.searches.limit}</li>
                    <li>Subscriptions: {usage.resources.subscriptions.current} / {usage.resources.subscriptions.limit}</li>
                    <li>Sources: {usage.resources.sources.current} / {usage.resources.sources.limit}</li>
                  </ul>
                </>
              ) : null}
              <UpgradePlanSection
                billing={billing}
                onRefresh={async () => {
                  const b = await api.billing.status().catch(() => null);
                  setBilling(b);
                }}
              />
            </section>
          </CollapsiblePanel>
        )}

        {canViewDiagnostics && (diagnostics || diagnosticsLoading || diagnosticsError) ? (
          <CollapsiblePanel id="diagnostics" storageScope="dashboard" title="Diagnostics" badge={diagnostics?.checks.length} defaultExpanded={false}>
            <DiagnosticsPanel
              diagnostics={diagnostics}
              loading={diagnosticsLoading}
              error={diagnosticsError}
              lastUpdatedAt={lastUpdatedAt}
              autoRefresh={diagnosticsAutoRefresh}
              autoRefreshing={diagnosticsRefreshing}
            />
          </CollapsiblePanel>
        ) : null}

        {summary?.generated_at ? (
          <p className="muted dashboard-generated-at">
            Summary generated {new Date(summary.generated_at).toLocaleString()}
          </p>
        ) : null}
      </AdminPageSections>
    </div>
  );
}
